export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;
  return function(this: unknown, ...args: Parameters<T>) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

class RequestCache {
  private cache = new Map<string, { data: any; timestamp: number; promise?: Promise<any> }>();
  private readonly defaultTTL = 30000; // 30秒缓存

  async get<T>(key: string, fetcher: () => Promise<T>, ttl = this.defaultTTL): Promise<T> {
    const cached = this.cache.get(key);
    const now = Date.now();

    if (cached && (now - cached.timestamp < ttl)) {
      return cached.data;
    }

    if (cached?.promise) {
      return cached.promise;
    }

    const promise = fetcher().catch(error => {
      this.cache.delete(key);
      throw error;
    });

    this.cache.set(key, {
      data: null,
      timestamp: now,
      promise
    });

    try {
      const data = await promise;
      this.cache.set(key, {
        data,
        timestamp: now
      });
      return data;
    } catch (error) {
      this.cache.delete(key);
      throw error;
    }
  }

  clear(key?: string) {
    if (key) {
      this.cache.delete(key);
    } else {
      this.cache.clear();
    }
  }

  clearExpired() {
    const now = Date.now();
    for (const [key, { timestamp }] of this.cache.entries()) {
      if (now - timestamp > this.defaultTTL) {
        this.cache.delete(key);
      }
    }
  }
}

export const requestCache = new RequestCache();

export function createRateLimiter(maxRequests: number, windowMs: number) {
  const requests: number[] = [];

  return {
    canMakeRequest(): boolean {
      const now = Date.now();
      const windowStart = now - windowMs;

      while (requests.length > 0 && requests[0] < windowStart) {
        requests.shift();
      }

      return requests.length < maxRequests;
    },

    recordRequest() {
      requests.push(Date.now());
    },

    waitTime(): number {
      if (requests.length < maxRequests) return 0;
      return Math.max(0, requests[0] + windowMs - Date.now());
    }
  };
}